// src/components/RadiologyViewer/utils/WindowLevelSyncManager.js

import Events from '@cornerstonejs/tools/enums/Events';
import { ToolGroupManager } from '@cornerstonejs/tools';

class WindowLevelSyncManager {
  constructor(renderingEngine, toolGroupId, voiUpdateCallback) {
    this.renderingEngine = renderingEngine;
    this.toolGroupId = toolGroupId;
    this.voiUpdateCallback = voiUpdateCallback;
    this.enabled = false;
    this.activeListeners = new Map();
    this.keyEnabled = false;
    this.boundKeyDownHandler = this.handleKeyDown.bind(this);
    this.boundKeyUpHandler = this.handleKeyUp.bind(this);
  }

  enable(viewportIds, isNormalView = false) {
    if (this.enabled) return;
    this.enabled = true;
    // Only these viewports (the MPR planes of THIS exam) sync together — never
    // unrelated viewports from other exams sharing the rendering engine.
    this.viewportIds = viewportIds;

    const elements = viewportIds.map((viewportId) => {
      const viewport = this.renderingEngine.getViewport(viewportId);
      return viewport ? viewport.element : null;
    }).filter(Boolean);
  
    elements.forEach((element, index) => {
      const viewportId = viewportIds[index];
      if (!this.activeListeners.has(viewportId)) {
        const dragHandler = () => {
          if (!this.enabled && !this.keyEnabled) return;
  
          const viewport = this.renderingEngine.getViewport(viewportId);
          if (!viewport) {
            console.error(`Viewport with ID ${viewportId} not found.`);
            return;
          }
  
          const { voiRange } = viewport.getProperties();
          if (!voiRange) return;

          const voi = {
            windowWidth: voiRange.upper - voiRange.lower,
            windowCenter: (voiRange.upper + voiRange.lower) / 2
          };
  
          if (isNormalView) {
            // Call the callback with windowWidth and windowCenter for normal view
            this.voiUpdateCallback(viewportId, {
              windowWidth: voi.windowWidth,
              windowCenter: voi.windowCenter
            });
          } else {
            // For MPR, sync across other viewports
            this.synchronizeWindowLevel(viewportId, voiRange);
          }
        
        };
  
        element.addEventListener(Events.MOUSE_DRAG, dragHandler);
        this.activeListeners.set(viewportId, dragHandler);
      }
    });
  
    // Add keyboard listeners
    document.addEventListener('keydown', this.boundKeyDownHandler);
    document.addEventListener('keyup', this.boundKeyUpHandler);
  }
  

  handleKeyDown(event) {
    if (event.key.toLowerCase() === 'w') {
      this.keyEnabled = true;
      
      // Enable WindowLevel tool when 'w' is pressed
      const toolGroup = ToolGroupManager.getToolGroup(this.toolGroupId);
      if (toolGroup) {
        toolGroup.setToolEnabled('WindowLevel');
      }
    }
  }

  handleKeyUp(event) {
    if (event.key.toLowerCase() === 'w') {
      this.keyEnabled = false;
      
      // Restore previous tool state
      const toolGroup = ToolGroupManager.getToolGroup(this.toolGroupId);
      if (toolGroup) {
        toolGroup.setToolDisabled('WindowLevel');
      }
    }
  }

  synchronizeWindowLevel(sourceViewportId, voiRange) {
    if (!this.renderingEngine) {
      console.error('Rendering engine not initialized.');
      return;
    }

    // Sync only within this manager's own viewport group (the same exam's MPR
    // planes), so window/level changes never bleed into other exams' viewports.
    (this.viewportIds || [])
      .filter((id) => id !== sourceViewportId)
      .forEach((id) => {
        const viewport = this.renderingEngine.getViewport(id);
        if (!viewport) return;
        viewport.setProperties({ voiRange });
        viewport.render();
      });
  }

  

  disable() {
    if (!this.enabled) return;
    this.enabled = false;

    const viewports = this.renderingEngine.getViewports();
    viewports.forEach(viewport => {
      const element = viewport.element;
      const handler = this.activeListeners.get(viewport.id);
      if (element && handler) {
        element.removeEventListener(Events.MOUSE_DRAG, handler);
        this.activeListeners.delete(viewport.id);
      }
    });

    // Remove keyboard listeners
    document.removeEventListener('keydown', this.boundKeyDownHandler);
    document.removeEventListener('keyup', this.boundKeyUpHandler);
    this.keyEnabled = false;
  }

  cleanup() {
    this.disable();
    this.activeListeners.clear();
  }
}

export default WindowLevelSyncManager;